import {
  Box,
  Checkbox
} from "@primer/react";
import clsx from "clsx";
import React, { createContext, ReactNode, useEffect, useMemo, useState } from "react";
import { Column, useTable } from "react-table";

import styles from "./DataTable.module.css";
import { dynamicSort, handleFilter, handleSearch } from "./DataTableHelper";
import Header from "./Header";
import Loading from "./Loading";
import Paging from "./Paging";
import Rows from "./Rows";
import Toolbar from "./Toolbar";
export enum eSortMode {
  ASC = "asc",
  DESC = "desc",
}

export interface IFocusCell {
  rowData: any;
  dataField: string;
}
export interface ISelectionProps {
  keyExpr?: string;
  mode: "multiple" | "single";
  onSelectionChanged: (keys: number[]) => void;
  selectedRowKeys?: number[];
}
interface ISortConfig {
  enable: boolean;
  field?: string;
  mode: eSortMode;
  onValueChanged?: (field: string, mode: eSortMode) => void;
}
interface IPaging {
  enable: boolean;
  pageSize?: number;
  pageSizeItems?: number[];
}

export interface IColumn {
  caption?: string;
  dataField?: string;
  isMainColumn?: boolean;
  fixed?: boolean;
  id?: string;
  cellRender?: (data: any) => JSX.Element;
  headerRender?: (data: any) => ReactNode;
  exportValue?: (data: any) => any;
  columns?: IColumn[];
  width?: number | string | undefined;
  minWidth?: number | string | undefined;
  maxWidth?: number | string | undefined;
  align?: "left" | "center" | "right";
  isAllowFocus?: boolean;
  isHideZeroValue?: boolean;
  isVisible?: boolean;
}
export interface ICheckDontSaveChange {
  fields: string[];
  rootDataSource: any[];
}
export interface IRangeSelectionKeyDownData {
  keyCode: string,
  rowKeys: any,
  dataFields: string[]
}
export interface IRangeSelectionProps {
  onKeyDown: (data: IRangeSelectionKeyDownData) => void
}
interface IFilterRowConfig {
  enable: boolean,
  onConditionChanged?: (cond: any) => void
}
type IComparisonOperator =
  | "equals"
  | "contains"
  | "greaterThan"
  | "lessThan"
  | "greaterThanOrEqual"
  | "lessThanOrEqual";
type ILogicOperator = "and" | "or";
export type IExpression = [string, IComparisonOperator, any];

export interface ICondition {
  field: string,
  operator: IComparisonOperator,
  value: any
}
interface IDataTableProps {
  width?: string;
  columns: IColumn[];
  data: any[];
  height?: string;
  title?: string;
  titleComponent?: React.ReactNode;
  subTitle?: string;
  subTitleComponent?: React.ReactNode;
  actionComponent?: React.ReactNode;
  isLoading?: boolean;
  paging?: IPaging;
  searchEnable?: boolean;
  sortConfig?: ISortConfig;
  exportEnable?: boolean;
  selection?: ISelectionProps;
  onFocuseCellChanged?: (data: IFocusCell) => void;
  focusedCell?: IFocusCell;
  checkDontSaveChange?: ICheckDontSaveChange;
  emptyComponent?: React.ReactNode;
  rangeSelection?: IRangeSelectionProps,
  isNoPaddingCell?: boolean;
  filterRow?: IFilterRowConfig;
  expandDetailRowTemplate?: any;
  onExpandedRowClick?: any;
  expandedRowKeys?: any

}
export interface IDataTableContext {
  props: IDataTableProps,
  searchKey?: string,
  setSearchKey?: (searchKey: string) => void,
  sortConfig?: ISortConfig,
  setSortConfig?: (sortConfig: ISortConfig) => void,
  sortByField: string,
  headerGroups: any[],
  allColumns: any[],
  filterdData?: any[];
  conditions: ICondition[],
  setConditions: (conditions: ICondition[]) => void,
  getTableBodyProps: any,
  pagedData: any[],
  rows: any[],
  prepareRow: any,
  selectedCells: any,
  setSelectedCells: any,
  isSelecting: boolean,
  setIsSelecting: (isSelecting: boolean) => void,
  startCell: any,
  setStartCell: (startCell: any) => void,
  pageIndex: number,
  setPageIndex: (pageIndex: number) => void,
  pageSize: number,
  setPageSize: (pageSize: number) => void,
  pageCount: number;


}
export const DataTableContainerContext = createContext<IDataTableContext>({
  props: {} as any,
  sortByField: "",
  headerGroups: [],
  allColumns: [],
  conditions: [],
  setConditions: {} as any,
  getTableBodyProps: {} as any,
  pagedData: [],
  rows: [],
  prepareRow: {} as any,
  selectedCells: {},
  setSelectedCells: {},
  isSelecting: false,
  setIsSelecting: {} as any,
  startCell: {},
  setStartCell: {} as any,
  pageIndex: 0,
  setPageIndex: {} as any,
  pageSize: 0,
  setPageSize: {} as any,
  pageCount: 0
});
const DataTable = (props: IDataTableProps) => {
  const { data, isLoading, paging } = props;
  const [logicOperator, setLogicOperator] = useState<ILogicOperator>("and");

  const [_searchKeyDelayed, _setSearchKeyDelayed] = useState("");
  const [_pageIndex, _setPageIndex] = useState(0);
  const [_pageSize, _setPageSize] = useState(props.paging?.pageSize ?? 50);

  const [_sortConfig, _setSortConfig] = useState(props.sortConfig);
  // Trạng thái vùng được chọn
  const [_selectedCells, _setSelectedCells] = useState(new Set());
  const [_isSelecting, _setIsSelecting] = useState(false);
  const [_startCell, _setStartCell] = useState<any>(null);
  const [_conditions, _setConditions] = useState<ICondition[]>([]);
  const _selectedRowKeys = useMemo(() => {
    return new Set(props.selection?.selectedRowKeys ?? []);
  }, [props.selection?.selectedRowKeys ?? []])
  const sortByField = useMemo(() => {
    return _sortConfig?.field ?? "";
  }, [_sortConfig]);
  const filterdData: any[] = useMemo(() => {
    let resultData: any[] = data;
    if (_searchKeyDelayed) {
      resultData = handleSearch(props.columns, props.data, _searchKeyDelayed);
    }
    if (sortByField)
      resultData = resultData.sort(
        dynamicSort(sortByField, _sortConfig?.mode ?? eSortMode.DESC)
      );

    if (_conditions.length > 0) {
      resultData = handleFilter(_conditions, resultData, logicOperator);
    }
    return resultData;
  }, [_searchKeyDelayed, data, props.columns, sortByField, _sortConfig?.mode, _conditions]);

  const pageCount = useMemo(() => {
    if (_pageSize <= 0) return filterdData.length;
    const c = Math.floor(filterdData.length / _pageSize);
    if (c * _pageSize < filterdData.length) return c + 1;
    return c;
  }, [filterdData.length, _pageSize]);

  const pagedData = useMemo(() => {
    if (paging?.enable === true) {
      const data = filterdData.slice(
        _pageSize * _pageIndex,
        _pageSize * _pageIndex + _pageSize
      );
      return data;
    }
    return filterdData;
  }, [filterdData, _pageIndex, _pageSize]);



  useEffect(() => {
    if (props.paging) {
      _setPageSize(props.paging.pageSize ?? 50);
    }
  }, [props.paging]);
  useEffect(() => {
    _setPageIndex(0);
  }, [sortByField]);
  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (_selectedCells.size > 0 && rows.length > 0) {
        let rowIndexs: number[] = [];
        let colIndexs: number[] = [];
        _selectedCells.forEach((cell: any) => {
          const [rowIndx, colIndex] = cell.split('-').map((c: any) => parseInt(c));
          if (!rowIndexs.includes(rowIndx)) rowIndexs.push(rowIndx);
          if (!colIndexs.includes(colIndex)) colIndexs.push(colIndex);

        })
        rowIndexs = Array.from(new Set(rowIndexs));
        colIndexs = Array.from(new Set(colIndexs));
        let rowKeys: number[] = [];
        let dataFields: string[] = [];
        rowIndexs.forEach(rowIndex => {
          const rowData = rows[rowIndex].original;
          if (rowData) rowKeys.push(rowData.id)
        })
        colIndexs.forEach(colIndex => {
          const cell = rows[0].cells[colIndex];
          if (cell) {
            const column: any = cell.column;
            dataFields.push(column?.dataField ?? "")
          }
        })


        if (props.rangeSelection && props.rangeSelection.onKeyDown) {
          props.rangeSelection.onKeyDown({
            dataFields,
            rowKeys,
            keyCode: e.key
          })
        }

      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Dọn dẹp sự kiện khi component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [_selectedCells]);


  // Kết thúc chọn vùng
  const handleMouseUp = () => {
    _setIsSelecting(false);
    _setStartCell(null);
  };


  const settingColum = (column: IColumn): Column => {
    const col: any = {
      ...column,
      width: column.width,
      minWidth: column.minWidth,
      columns:
        column.columns && column.columns.length > 0
          ? column.columns.map((x) => settingColum(x))
          : undefined,
      Header: column.caption ?? "",
      accessor: column.dataField ?? column.id ?? "",
    };
    return col;
  };
  const columns: any[] = useMemo(() => {
    let col = props.columns.filter(x => x.isVisible !== false).map((x) => {
      return settingColum(x);
    });
    if (props.selection?.mode === "multiple") {
      const selectionCol: any = {
        id: "selection",
        width: "40px",
        align: "center",
        cellRender: (data: any) => {
          const id = data.id;
          return (
            <Checkbox
              checked={_selectedRowKeys.has(id)}
              disabled={data.disabled}
              onChange={(e) => {
                if (e.target.checked) {
                  props.selection?.onSelectionChanged([
                    ...(props.selection?.selectedRowKeys ?? []),
                    id,
                  ]);
                } else {
                  props.selection?.onSelectionChanged([
                    ...(props.selection?.selectedRowKeys ?? []).filter(
                      (x) => x !== id
                    ),
                  ]);
                }
              }}
            />
          );
        },
        headerRender: () => {
          return (
            <Checkbox
              checked={
                filterdData.filter((g) => !g.disabled).length > 0 &&
                filterdData
                  .filter((g) => !g.disabled)
                  .find(
                    (x) =>
                      !(props.selection?.selectedRowKeys ?? []).includes(x.id)
                  ) === undefined
              }
              onChange={(e) => {
                const ids = filterdData
                  .filter((g) => !g.disabled)
                  ?.map((x) => x.id);
                if (e.target.checked) {
                  props.selection?.onSelectionChanged(
                    Array.from(
                      new Set([
                        ...(props.selection?.selectedRowKeys ?? []),
                        ...ids,
                      ])
                    )
                  );
                } else {
                  props.selection?.onSelectionChanged(
                    Array.from(
                      new Set(
                        (props.selection?.selectedRowKeys ?? []).filter(
                          (x) => !ids.includes(x)
                        )
                      )
                    )
                  );
                }
              }}
            />
          );
        },
      };
      return [selectionCol, ...col];
    }
    return col;
  }, [props.columns, props.selection?.mode]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, allColumns } =
    useTable<any>({ columns, data: pagedData });
  const contextValue: IDataTableContext = {
    props,
    searchKey: _searchKeyDelayed,
    setSearchKey: _setSearchKeyDelayed,
    sortConfig: _sortConfig,
    setSortConfig: _setSortConfig,
    sortByField,
    headerGroups,
    allColumns,
    filterdData,
    conditions: _conditions,
    setConditions: _setConditions,
    getTableBodyProps,
    pagedData,
    rows,
    prepareRow,
    selectedCells: _selectedCells,
    setSelectedCells: _setSelectedCells,
    isSelecting: _isSelecting,
    setIsSelecting: _setIsSelecting,
    setStartCell: _setStartCell,
    startCell: _startCell,
    pageIndex: _pageIndex,
    pageSize: _pageSize,
    setPageIndex: _setPageIndex,
    setPageSize: _setPageSize,
    pageCount
  };
  return (
    <Box>
      <DataTableContainerContext.Provider value={contextValue}>
        <Toolbar />
        <Box
          id="warpper"
          sx={{
            position: "relative",
            paddingBottom: paging?.enable === true ? "41px" : "0",
          }}
        >
          <Box
            className={clsx(styles.container)}
            sx={{
              overflow: "auto",
              height: props.height ?? "auto",
              marginBottom: "0", 
              borderBottom: paging?.enable === true ? "none" : "1px solid #d0d7de", 
            }}
          >
            <table
              {...getTableProps()}
              className={clsx(
                styles.myTable,
                props.isNoPaddingCell ? styles.noPadding : undefined,
                props.rangeSelection ? styles.rangeSelection : undefined,
                paging?.enable === true ? styles.hasPaging : ""
              )}
              onMouseUp={props.rangeSelection ? handleMouseUp : undefined}
              style={{ borderBottom: "1px solid #d0d7de" }} 
            >
              <Header />
              {isLoading && <Loading />}
              {!isLoading && (
                <>
                  <Rows />
                </>
              )}
            </table>
          </Box>
          {paging?.enable === true && pageCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: "0",
                left: "0",
                right: "0",
                zIndex: "1", 
                borderLeft: "1px solid #d0d7de",
                borderRight: "1px solid #d0d7de",
                borderBottom: "1px solid #d0d7de",
                borderBottomLeftRadius: "0.375rem",
                borderBottomRightRadius: "0.375rem",
              }}
            >
              <Paging />
            </Box>
          )}
        </Box>
      </DataTableContainerContext.Provider>
    </Box>
  );
};
DataTable.Toolbar = Toolbar;
DataTable.Header = Header;
DataTable.Loading = Loading;
DataTable.Rows = Rows;
DataTable.Paging = Paging;
export default DataTable;
